import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "487",
  "slug": "how-do-ocean-currents-work-global-conveyor-belt",
  "title": "How Do Ocean Currents Work? The Global Conveyor Belt Explained",
  "excerpt": "Ocean currents move more heat around the planet than almost anything else. Here is how wind, the Earth's spin, and differences in salt and temperature keep the seas in constant motion.",
  "date": "2026-06-19",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1518837695005-2083093ee35b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxvY2VhbiUyMHdhdmVzJTIwc2VhfGVufDF8MHx8fDE3ODE4NTY3ODl8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "6 min",
  "author": "EarthGuessr Team",
  "tldr": "Ocean currents are driven by two engines: wind pushes surface currents, which the Earth's rotation bends into great looping gyres, while differences in temperature and saltiness drive a slow, deep circulation often called the global conveyor belt. Together they move heat from the tropics toward the poles and shape regional climates.",
  "content": [
    {
      "type": "paragraph",
      "text": "The ocean is never still. Beneath the waves, rivers of water thousands of kilometres long are constantly on the move, carrying warmth from the tropics toward the poles and cold water back again. These ocean currents do as much to set the climate of a coastline as its latitude does — which is why one city can be mild and green at the same distance from the equator where another is frozen."
    },
    {
      "type": "heading",
      "text": "Two Kinds of Ocean Current"
    },
    {
      "type": "paragraph",
      "text": "Oceanographers separate currents into two systems that work together. Surface currents are driven by the wind and stir the top few hundred metres of the sea. Deep currents are driven by differences in water density and move the vast, cold body of water far below. Understanding the ocean means understanding both, and how they connect into a single loop."
    },
    {
      "type": "heading",
      "text": "Surface Currents: Wind and the Earth's Spin"
    },
    {
      "type": "paragraph",
      "text": "Near the surface, steady winds drag the water along with them. The trade winds push the tropics westward; the westerlies push the mid-latitudes eastward. But the moving water does not travel in a straight line, because the Earth is rotating beneath it. This deflection, the Coriolis effect, bends currents to the right in the Northern Hemisphere and to the left in the Southern."
    },
    {
      "type": "paragraph",
      "text": "The result is that surface currents curve around the ocean basins into huge, slow loops called gyres. There are five major subtropical gyres, and they explain why the same ocean can carry warm water poleward on one side and cool water back toward the equator on the other. The narrow, fast, warm currents on the western edge of these gyres — like the Gulf Stream in the Atlantic and the Kuroshio in the Pacific — are among the most powerful flows on the planet."
    },
    {
      "type": "heading",
      "text": "Deep Currents: Temperature and Salt"
    },
    {
      "type": "paragraph",
      "text": "The second engine has nothing to do with wind. Cold water is denser than warm water, and salty water is denser than fresh. Where the surface ocean becomes both cold and salty — especially in the far North Atlantic and around Antarctica — the water grows heavy enough to sink, plunging into the deep ocean. This sinking pulls in more water behind it and pushes the deep water slowly along the sea floor."
    },
    {
      "type": "paragraph",
      "text": "This density-driven flow is called thermohaline circulation, from the words for heat and salt. It is astonishingly slow: a single parcel of water may take centuries to complete the journey. Linked together, the surface and deep currents form a single planet-spanning loop sometimes pictured as a global conveyor belt, ferrying heat, dissolved gases and nutrients between every ocean basin."
    },
    {
      "type": "heading",
      "text": "Why Ocean Currents Matter"
    },
    {
      "type": "paragraph",
      "text": "Currents are one of the main reasons the planet is liveable, because they spread the sun's uneven heating around. Their effects are everywhere:"
    },
    {
      "type": "list",
      "items": [
        "Climate: warm currents keep places like north-west Europe far milder than their high latitude would suggest, while cold currents can leave coastlines cool and foggy.",
        "Weather: the temperature of the sea surface feeds storms and influences rainfall on nearby land.",
        "Marine life: where deep, cold water rises to the surface — upwelling — it brings nutrients that fuel some of the richest fishing grounds on Earth.",
        "Heat balance: by moving warmth from the equator toward the poles, currents stop the tropics from overheating and the poles from freezing solid."
      ]
    },
    {
      "type": "heading",
      "text": "Seeing the Ocean in Motion"
    },
    {
      "type": "paragraph",
      "text": "You cannot see a current in a single photograph, but you can read its fingerprints: a coastline that is mild for its latitude, fog where cold water meets warm air, or a rich fishery offshore. Knowing how the great currents flow adds a hidden layer to the map, turning the blank blue of the ocean into a system you can reason about — handy context whenever you are puzzling over where in the world a coastal scene belongs in EarthGuessr."
    }
  ]
};
